import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { chordInventory, normalizeKey, transposeKey } from '../../lib/chords'
import { toTitleCase } from '../../lib/format'
import { usePlayer } from '../../lib/player'
import { Shell } from '../../components/Shell'
import ChordChart from '../../components/ChordChart'
import { Check, Player, Spinner, Waveform, isVideo, mmss } from '../../components/ui'

/**
 * How to play someone else's song.
 *
 * Read-only, and reachable by anyone the song's reach lets in — that is the
 * point of allowing covers at all. RLS decides: this asks for the song and its
 * sections and gets back nothing at all if the writer hasn't opened it.
 *
 * The transpose control does not touch the database. Chords are stored as
 * degrees, so "in G" is a rendering argument, not an edit — a player can move
 * the whole chart to fit their singer without changing the writer's song.
 */
export default function Learn() {
  const { id } = useParams()
  const nav = useNavigate()
  const { user } = useSession()
  const player = usePlayer()

  const [song, setSong] = useState(null)
  const [sections, setSections] = useState([])
  const [writers, setWriters] = useState([])
  const [ready, setReady] = useState(false)
  const [src, setSrc] = useState(null)
  const [err, setErr] = useState(null)
  const [lockReason, setLockReason] = useState(null)

  const [mode, setMode] = useState(
    () => localStorage.getItem('beseter.chart.mode') || 'chords'
  )
  const [steps, setSteps] = useState(0)

  useEffect(() => { localStorage.setItem('beseter.chart.mode', mode) }, [mode])
  useEffect(() => { setSteps(0) }, [id])

  const load = useCallback(async () => {
    const [{ data: s, error }, { data: sec }, { data: cw }] = await Promise.all([
      supabase.from('songs').select('*, users:songwriter_id(first_name, last_name)')
        .eq('id', id).maybeSingle(),
      supabase.from('song_sections').select('*').eq('song_id', id).order('order_position'),
      supabase.from('song_cowriters').select('cowriter_name, split_percentage')
        .eq('song_id', id).order('is_primary', { ascending: false }),
    ])
    if (error) setErr(readableError(error))
    setSong(s ?? null)
    setSections(sec ?? [])
    setWriters(cw ?? [])
    setReady(true)

    // RLS returning nothing is ambiguous — never shared this far, or a
    // withdrawal locked it for this specific church. Only the second one has
    // anything to tell a worship team, so it's the one worth asking about.
    if (!s) {
      supabase.rpc('song_church_lock_reason', { p_song: id })
        .then(({ data }) => setLockReason(data ?? null))
    }
  }, [id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!song?.r2_object_key) return
    supabase.functions.invoke('r2-playback-url', { body: { song_id: song.id } })
      .then(({ data }) => setSrc(data?.url ?? null))
  }, [song?.id, song?.r2_object_key])

  const written = normalizeKey(song?.key) ?? 'C'
  const sounding = useMemo(() => transposeKey(written, steps), [written, steps])
  const inventory = useMemo(
    () => chordInventory(sections, { mode, key: sounding }),
    [sections, mode, sounding]
  )

  // Same control drives both: the chart re-renders in the new key, and if
  // this song is the one actually playing, the audio pitch-shifts to match.
  // Purely local until you press play — nothing here touches the database.
  const isPlayingThisSong = !isVideo(song?.media_mime) && player.isCurrent(song?.id)
  useEffect(() => {
    if (isPlayingThisSong) player.setSemitones(steps)
  }, [steps, isPlayingThisSong, player])

  if (!ready) return <Shell><Spinner /></Shell>
  if (!song) {
    return (
      <Shell back="/collective" title="The collective">
        {lockReason === 'withdrawn' ? (
          <>
            <h2 className="q">This song is locked.</h2>
            <p className="sub">
              Access to this song is currently locked due to a request by the song's owner.
              Contact your church to discuss.
            </p>
          </>
        ) : (
          <>
            <h2 className="q">This song isn't open to you.</h2>
            <p className="sub">
              The writer may have pulled it back in, or it was never shared this far.
            </p>
          </>
        )}
      </Shell>
    )
  }

  const mine = song.songwriter_id === user.id
  const writerName = [song.users?.first_name, song.users?.last_name].filter(Boolean).join(' ')
  const hasChords = sections.some((s) => (s.chords ?? []).length > 0)
  const video = isVideo(song.media_mime)
  const isCurrentTrack = !video && player.isCurrent(song.id)
  const audioPlaying = isCurrentTrack && player.playing
  const audioPos = isCurrentTrack ? player.pos : 0
  const audioLen = isCurrentTrack ? player.len : (song.duration_seconds || 0)
  const audioFailed = isCurrentTrack && player.failed
  const toggleAudio = () => {
    if (isCurrentTrack) return player.toggle()
    if (!src) return
    player.play({
      id: song.id, src, mime: song.media_mime,
      title: song.title || 'Untitled', writer: writerName,
      writerId: song.songwriter_id,
      duration: song.duration_seconds,
    })
  }

  return (
    <Shell back={mine ? '/me' : '/home'} title={mine ? 'Your songs' : 'Home'} tabs>
      <div className="eyebrow">Learn</div>
      <h2 className="q">{toTitleCase(song.title) || 'Untitled'}</h2>
      <p className="sub">
        {writers.length > 0
          ? writers.map((w) => w.cowriter_name).join(' · ')
          : writerName || 'A writer'}
      </p>

      <div className="chart-bar">
        <div className="seg" role="group" aria-label="Chart notation">
          <button className={`seg-b${mode === 'chords' ? ' on' : ''}`}
                  onClick={() => setMode('chords')} aria-pressed={mode === 'chords'}>
            Chords
          </button>
          <button className={`seg-b${mode === 'numbers' ? ' on' : ''}`}
                  onClick={() => setMode('numbers')} aria-pressed={mode === 'numbers'}>
            Numbers
          </button>
        </div>

        {/* Numbers are already key-independent — that is what they're for — so
            transposing them would move nothing on screen. */}
        {mode === 'chords' && (
          <div className="transpose">
            <button className="iconbtn" onClick={() => setSteps(Math.max(-player.maxSemitones, steps - 1))}
                    disabled={steps <= -player.maxSemitones}
                    aria-label="Transpose down a semitone">−</button>
            <span className={`transpose-k${Math.abs(steps) > 3 ? ' transpose-k-extended' : ''}`}>
              {sounding}
              {steps !== 0 && <small>from {written}</small>}
            </span>
            <button className="iconbtn" onClick={() => setSteps(Math.min(player.maxSemitones, steps + 1))}
                    disabled={steps >= player.maxSemitones}
                    aria-label="Transpose up a semitone">+</button>
          </div>
        )}
      </div>

      {mode === 'numbers' && (
        <p className="small" style={{ marginTop: -6, marginBottom: 14 }}>
          Numbers work in every key. 1 is {written}, 4 is the fourth, 5 the fifth —
          the same chart works whoever is singing.
        </p>
      )}

      {steps !== 0 && (
        <p className="small" style={{ marginTop: -6, marginBottom: 14 }}>
          Showing this in <b>{sounding}</b>. The writer's key is {written} —
          nothing here is changed for them.
          {isPlayingThisSong && ' The recording is playing pitch-shifted to match.'}
          {Math.abs(steps) > 3 && ' This far out, the vocal starts to sound a bit different.'}{' '}
          <button className="linkish" onClick={() => setSteps(0)}>Back to {written}</button>
        </p>
      )}

      {inventory.length > 0 && (
        <div className="chart-inv">
          <span className="chart-inv-cap">Chords in this song</span>
          <div className="chips">
            {inventory.map((c) => <span key={c} className="chip chip-static">{c}</span>)}
          </div>
        </div>
      )}

      {song.scripture_refs?.length > 0 && (
        <p className="feedref" style={{ marginTop: 14 }}>{song.scripture_refs[0]}</p>
      )}

      {song.r2_object_key && (
        video ? (
          <Player src={src} mime={song.media_mime} duration={song.duration_seconds}
                  filename={song.media_filename} />
        ) : (
          // Same audio element the mini player uses (see lib/player.jsx) —
          // starting playback here is what makes the mini bar show up when
          // you leave this screen, instead of stopping the moment you tap away.
          <div className="player">
            <button className="play" onClick={toggleAudio} disabled={!src || audioFailed}
                    aria-label={audioPlaying ? 'Pause' : 'Play'}>
              {audioPlaying ? '❚❚' : '▶'}
            </button>
            <span className="ptime">{src ? mmss(audioPos) : '—'}</span>
            {/* Only the track that is actually loaded can be scrubbed — seeking
                a song the element isn't holding would silently do nothing. */}
            <Waveform pos={audioPos} len={audioLen} onSeek={player.seek}
                      disabled={!isCurrentTrack || !src || audioFailed} />
            <span className="ptime ptime-total">{src && audioLen ? mmss(audioLen) : '—'}</span>
          </div>
        )
      )}

      {!hasChords && (
        <p className="small" style={{ marginBottom: 16 }}>
          No chords on this one yet — the words are below.
          {mine && ' Open the song to put chords over them.'}
        </p>
      )}

      {sections.length === 0
        ? <p className="small">This song has no written sections — it may be a voice memo.</p>
        : <ChordChart sections={sections} mode={mode} songKey={sounding} />}

      {sections.length > 0 && (
        <>
          <div className="hr" />
          <button className="btn btn-quiet" onClick={() => nav(`/songs/${song.id}/sheet`)}>
            Take a chart or lyric sheet
          </button>
          <p className="small center" style={{ marginTop: 10 }}>
            Prints, or saves as a PDF, with the writers' names and the usage note on it.
          </p>
        </>
      )}

      {song.allow_covers && !mine && (
        <>
          <div className="hr" />
          <div className="tile">
            <div className="tile-head">
              <div>
                <h3>You can cover this</h3>
                <p className="small">
                  {writerName || 'The writer'} has said another church may record
                  their own version, so long as they're credited.
                </p>
              </div>
              <span className="tile-tick"><Check /></span>
            </div>
          </div>
        </>
      )}

      {mine && (
        <>
          <div className="hr" />
          <button className="btn btn-quiet" onClick={() => nav(`/songs/${song.id}/2`)}>
            Edit the words and chords
          </button>
        </>
      )}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
